import { ChevronDown, ChevronRight } from 'lucide-react'
import { ReactNode, useState } from 'react'

import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet'

// セクション型定義
export type Section = {
  id: string
  label: string
  href: string
  page?: number | null
  subitems?: Section[]
  active?: boolean
  isCurrent?: boolean
  isParentOfCurrent?: boolean
}

// セクションアイテム型定義
export type SectionItem<T = unknown> = {
  id: string | number
  content: T
  onClick: () => void
}

// グループ化されたアイテム型定義
export type GroupedItems<T = unknown> = {
  section: Section
  items: SectionItem<T>[]
}

// SectionListSheet のプロパティ
type SectionListSheetProps = {
  open: boolean
  onClose: () => void
  title: string
  icon?: ReactNode
  side?: 'left' | 'right'
  headerContent?: ReactNode
  children: ReactNode
}

export const SectionListSheet = ({
  open,
  onClose,
  title,
  icon,
  side = 'left',
  headerContent,
  children,
}: SectionListSheetProps) => {
  return (
    <Sheet open={open} onOpenChange={(open) => !open && onClose()}>
      <SheetContent side={side} className="w-full sm:max-w-md overflow-y-auto">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            {icon}
            <span>{title}</span>
          </SheetTitle>
          {headerContent && <div className="pt-2">{headerContent}</div>}
        </SheetHeader>
        <div className="mt-4">{children}</div>
      </SheetContent>
    </Sheet>
  )
}

// SectionListItem のプロパティ
type SectionListItemProps = {
  item: Section
  onClick: (href: string) => void
  level?: number
}

export const SectionListItem = ({ item, onClick, level = 0 }: SectionListItemProps) => {
  const [isOpen, setIsOpen] = useState(level === 0)
  const hasSubitems = item.subitems && item.subitems.length > 0

  const handleClick = () => {
    if (hasSubitems) {
      setIsOpen(!isOpen)
    }
    onClick(item.href)
  }

  return (
    <div>
      <button
        onClick={handleClick}
        className={`w-full text-left px-3 py-2 rounded-md transition-colors ${
          item.active ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'
        }`}
        style={{ paddingLeft: `${level * 16 + 12}px` }}
      >
        <div className="flex items-center gap-2">
          {hasSubitems && (
            <span className="shrink-0">{isOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />}</span>
          )}
          <span className="flex-1 text-sm">{item.label}</span>
          {item.page && <span className="text-xs text-muted-foreground shrink-0">p.{item.page}</span>}
        </div>
      </button>
      {hasSubitems && isOpen && (
        <div className="mt-1">
          {item.subitems!.map((subitem) => (
            <SectionListItem key={subitem.id} item={subitem} onClick={onClick} level={level + 1} />
          ))}
        </div>
      )}
    </div>
  )
}

// GroupedSectionList のプロパティ
type GroupedSectionListProps<T> = {
  groups: GroupedItems<T>[]
  emptyMessage: string
  renderItem: (item: SectionItem<T>) => ReactNode
  onSectionClick?: (href: string) => void
}

export const GroupedSectionList = <T,>({
  groups,
  emptyMessage,
  renderItem,
  onSectionClick,
}: GroupedSectionListProps<T>) => {
  if (groups.length === 0) {
    return <div className="text-center text-muted-foreground py-8">{emptyMessage}</div>
  }

  return (
    <div className="space-y-4">
      {groups.map((group) => (
        <div key={group.section.id} className="space-y-2">
          {/* セクションヘッダー */}
          <button
            onClick={() => onSectionClick?.(group.section.href)}
            className={`w-full text-left px-3 py-2 rounded-md bg-muted/50 ${
              onSectionClick ? 'hover:bg-muted cursor-pointer' : ''
            }`}
            disabled={!onSectionClick}
          >
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">{group.section.label}</span>
              {group.section.page && <span className="text-xs text-muted-foreground">p.{group.section.page}</span>}
            </div>
          </button>

          {/* アイテムリスト */}
          <div className="space-y-1">{group.items.map((item) => renderItem(item))}</div>
        </div>
      ))}
    </div>
  )
}
